import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Ticket, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';

export default function JoinPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRedeem = () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('postLoginRedirect', window.location.pathname);
      navigate('/auth/signin');
      return;
    }

    if (!code) return;
    setIsRedeeming(true);
    setError(null);

    api.post<{ streamId?: number }>('/api/invites/join', { code: code.toUpperCase() })
      .then((response) => {
        if (response.success) {
          setIsSuccess(true);
          const streamId = (response.data as { streamId?: number })?.streamId;
          setTimeout(() => {
            if (streamId) navigate(`/streams/${streamId}`);
            else navigate('/streams');
          }, 2000);
        } else {
          setError(response.error?.message || 'Failed to redeem invite code.');
        }
      })
      .catch(() => setError('Failed to redeem invite code.'))
      .finally(() => setIsRedeeming(false));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Ticket className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Stream Invite</CardTitle>
          <CardDescription>You've been invited to watch a stream</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {isSuccess ? (
            <div className="flex flex-col items-center space-y-3 py-4 text-center">
              <CheckCircle className="h-10 w-10 text-green-500" />
              <p className="font-medium">Access granted!</p>
              <p className="text-sm text-muted-foreground">Redirecting you to the stream…</p>
            </div>
          ) : (
            <>
              <div className="rounded-lg border p-4 text-sm">
                <p className="text-muted-foreground mb-1">Invite code</p>
                <p className="font-mono font-medium tracking-wider">{code}</p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button className="w-full" onClick={handleRedeem} disabled={isRedeeming}>
                {isRedeeming ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redeeming…</>
                ) : !isAuthenticated ? (
                  'Sign in to redeem'
                ) : (
                  <><Ticket className="mr-2 h-4 w-4" />Redeem & Watch</>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
